import { Button } from "$components/button/button";
import { LinkButton } from "$components/button/link-button";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { WarningIcon } from "$components/icons";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { formatDate } from "$routes/inventory/_lib/formatting";
import { LocationSelect } from "$routes/inventory/_lib/location-select";
import { linkToStockTake } from "$routes/inventory/links";
import { gql } from "@apollo/client";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import {
  useInitiateStockTakeModalMutation,
  useOutstandingStockTakesQuery,
} from "./__generated__/initiate-stock-take-modal.gql";

export interface InitiateStockTakeModalProps {
  locationId?: string | null;
  open: boolean;
  onClose(): void;
}

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.warning.main,
    marginRight: "10px",
  },
  modalBody: {
    overflow: "hidden",
  },
}));

gql`
  mutation initiateStockTakeModal($input: InitiateStockTakeInput!) {
    initiateStockTake(input: $input) {
      id
    }
  }
`;

gql`
  query outstandingStockTakes($input: StockTakesInput!) {
    stockTakes(input: $input, options: { pageSize: 1 }) {
      items {
        id
        createdAt
        finalisedAt
      }
    }
  }
`;

function InitiateStockTakeModal({
  onClose,
  open,
  locationId: defaultLocationId = null,
}: InitiateStockTakeModalProps) {
  const navigate = useNavigate();
  const { facilityId } = useUser();

  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [initiateStockTake, { loading: initiateLoading }] =
    useInitiateStockTakeModalMutation();

  const [locationId, setLocationId] = useState<string | null>(
    defaultLocationId,
  );

  const { data: existingStockTakes, loading: existingStockTakesLoading } =
    useOutstandingStockTakesQuery({
      variables: {
        input: {
          facilityId,
          locationId,
        },
      },
      skip: !locationId,
      fetchPolicy: "network-only",
    });

  const {
    id: lastTakeId,
    createdAt: lastTakeCreatedAt,
    finalisedAt: lastTakeFinalisedAt,
  } = existingStockTakes?.stockTakes?.items?.[0] || {};

  const loading = initiateLoading || existingStockTakesLoading;
  const disabled = loading || !locationId;
  const canResumeStockTake =
    lastTakeId && !lastTakeFinalisedAt && !existingStockTakesLoading;

  const onInitiateStockTakeClick = async () => {
    const result = await initiateStockTake({
      variables: { input: { facilityId, locationId } },
      onError: (error) => {
        toast.error(error.message);
      },
    });

    navigate(linkToStockTake(result.data.initiateStockTake.id));
  };

  return (
    <DialogContainer
      open={open}
      onClose={() => {
        onClose();
      }}
      data-testid="initiate-stocktake-modal"
    >
      <DialogBody className={classes.modalBody}>
        <Box display="flex" alignItems="center" pb={2}>
          <WarningIcon
            viewBox="0 0 32 32"
            fontSize="large"
            className={classes.icon}
          />
          <Typography variant="h3" component="h1">
            <FormattedMessage
              id="inventory.stockTake.initiateModal.title"
              defaultMessage="Initiate a stocktake"
            />
          </Typography>
        </Box>
        <LocationSelect
          id="locationId"
          value={locationId}
          onChange={setLocationId}
          label={formatMessage({
            id: "inventory.stockTake.initiateModal.locationSelect",
            defaultMessage: "Location",
          })}
        />
        {canResumeStockTake && (
          <Alert severity="info">
            <Typography>
              <FormattedMessage
                id="inventory.stockTake.initiateModal.existingStockTakeMessage"
                defaultMessage="You have an incomplete stocktake at this location that was started on { createdAt }. Would you like to continue it or discard it and start a new one?"
                values={{
                  createdAt: formatDate(lastTakeCreatedAt),
                }}
              />
            </Typography>
          </Alert>
        )}
      </DialogBody>
      <DialogFooter>
        <Button color="secondary" onClick={onClose}>
          <FormattedMessage
            id="inventory.stockTake.initiateModal.cancel"
            defaultMessage="Cancel"
          />
        </Button>

        <Button
          color={canResumeStockTake ? "error" : "primary"}
          loading={loading}
          disabled={disabled}
          onClick={onInitiateStockTakeClick}
          data-testid="inventory.stockTake.initiate"
        >
          {canResumeStockTake ? (
            <FormattedMessage
              id="inventory.stockTake.initiateModal.discard"
              defaultMessage="Discard and Start New"
            />
          ) : (
            <FormattedMessage
              id="inventory.stockTake.initiateModal.selectLocation"
              defaultMessage="Select Location"
            />
          )}
        </Button>

        {canResumeStockTake ? (
          <LinkButton
            to={linkToStockTake(lastTakeId)}
            loading={loading}
            disabled={disabled}
          >
            <FormattedMessage
              id="inventory.stockTake.initiateModal.joinExisting"
              defaultMessage="Continue Existing"
            />
          </LinkButton>
        ) : null}
      </DialogFooter>
    </DialogContainer>
  );
}

export default InitiateStockTakeModal;
